import { createStore, createEffect, createEvent, combine } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtilsV2 } from '@shared/utils';
import {
  AccountsApi,
  IntegrationsApi,
  RequestStrategyEnum,
  type ApiPaginate,
  type AccountForIntegrationsRequest,
  type SetAccountTypeRequest,
  type DisconnectAccountRequest,
  type AccountForIntegrationsResponse,
  type WithSignal,
} from '@shared/api';

const getAccountsForIntegrationsEffect = createEffect(({ signal, ...request }: WithSignal<AccountForIntegrationsRequest> = {}) =>
  AccountsApi.getAccountsForIntegrations(request, signal),
);
const connectAccountsEffect = createEffect((request: SetAccountTypeRequest) => IntegrationsApi.setAccountType(request));
const disconnectAccountEffect = createEffect((request: DisconnectAccountRequest) => IntegrationsApi.disconnectAccount(request));
const clearEvent = createEvent();

const $dataStore = createStore<ApiPaginate<AccountForIntegrationsResponse[]> | null>(null)
  .on(getAccountsForIntegrationsEffect.done, (current, data) => data.result ?? current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getAccountsForIntegrationsEffect.pending, (_, data) => data)
  .on(getAccountsForIntegrationsEffect.fail, (_, data) => ApiUtilsV2.abortApiLoadingFailCaseTrigger(data.error))
  .on(getAccountsForIntegrationsEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getAccountsForIntegrationsEffect, () => null)
  // .on(getAccountsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getAccountsForIntegrationsEffect.fail, () => 'Something went wrong while fetching Accounts For Integrations. Please try again.')
  .on(getAccountsForIntegrationsEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(connectAccountsEffect, () => null)
  // .on(connectAccountsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(connectAccountsEffect.fail, () => 'Something went wrong while connecting Account. Please try again.')
  .on(connectAccountsEffect.done, () => null)
  .on(disconnectAccountEffect, () => null)
  // .on(disconnectAccountEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(disconnectAccountEffect.fail, () => 'Something went wrong while disconnecting Account. Please try again.')
  .on(disconnectAccountEffect.done, () => null);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getAccountsApi = ApiUtilsV2.wrapperApi(getAccountsForIntegrationsEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getAccountsApi.cancel());

export const $accountForIntegrationsStore = {
  events: {
    getAccountsForIntegrations: getAccountsApi.trigger,
    connectAccounts: connectAccountsEffect,
    disconnectAccount: disconnectAccountEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
